/**
 * Session identity — who the host app says is using the sandbox (BOOT-01..03).
 *
 * The client's mobile app opens this SPA inside its in-app browser and hands
 * identity over on the boot URL, exactly once:
 *
 *     ?EmployeeId=1234&Role=XXX_XXX
 *
 * Two properties of this app dictate the shape of this module:
 *
 * 1. **Those params do not survive navigation.** Every `navigate()` in the
 *    player either targets a bare path or builds a fresh `URLSearchParams`
 *    (`src/player/LessonStart.tsx`, `src/player/TourOverlay.tsx`), and the
 *    re-attach workaround in `src/player/TaskOverlay.tsx` only restores values
 *    declared in lesson content JSON, and only while a `?sim=` run is active —
 *    it cannot carry boot identity. So identity is captured ONCE at module
 *    load, before `RouterProvider` mounts, and held for the session. This is
 *    not a new pattern: `src/i18n.ts:9` reads `?lang=` from
 *    `window.location.search` at module scope for exactly the same reason.
 *
 * 2. **There is no React context anywhere in this codebase**, and none is
 *    introduced here. A plain module holding a captured value matches
 *    `src/screens/ordering/use-role.ts`, which documents the same
 *    provider-free choice.
 *
 * Identity is never read back from storage — on a shared store device that
 * would attribute one employee's completions to another (TECH-SPEC §2.1).
 */
import type { SessionIdentity } from '@shared/api-types'

export type { SessionIdentity }

/**
 * The canonical spelling of each boot param. The host app is a black box and
 * has been seen sending these names with differing case per platform — the
 * same hazard `./role-labels.ts` documents for role VALUES (PITFALLS Pitfall
 * 12) — so `lookup()` below matches them case-insensitively.
 *
 * KNOWN COLLISION, accepted deliberately (claim quick-kayinleong-026).
 * Lowercase `?role=` is a DIFFERENT vocabulary that also exists in this app:
 * `src/screens/ordering/use-role.ts:61` reads it for `StaffRole` demo slugs
 * (`team-lead`, `storekeeper`, …) so a lesson step can demo a screen as
 * another role, and `src/schema/index.ts:256` lets content set it. Matching
 * case-insensitively therefore means a boot URL carrying a demo `?role=` can
 * set the learner's identity role, which is then written to the Sheet.
 *
 * Two things bound that: identity is captured ONCE at module load (see the
 * file header), so an in-session demo link cannot change it; and where BOTH
 * spellings are present, the exact-cased name wins — see `lookup()`. Without
 * that tie-break the winner would depend on parameter order, which the host
 * app does not control.
 */
/**
 * Exported because identity has to be RE-ATTACHED to the URL after navigation,
 * not just read from it once — see `./identity-url.ts`. Two copies of these
 * strings would be two chances to drift, and a drifted name reads as "no
 * learner" rather than failing loudly.
 */
export const EMPLOYEE_PARAM = 'EmployeeId'
export const ROLE_PARAM = 'Role'

/**
 * Parses identity out of a URL query string.
 *
 * Pure by design: it takes the search string rather than touching `window`, so
 * every rule below is testable in the node-based `unit` project too. A leading
 * `?` is optional — `URLSearchParams` strips it. A repeated parameter resolves
 * to its first occurrence, which is what `URLSearchParams.get()` returns.
 *
 * Rules:
 * - Param NAMES match in any letter case (`EmployeeId`, `employeeid`, `EMPLOYEEID`).
 * - Both values are trimmed.
 * - `EmployeeId` missing, empty, or whitespace-only → `null`. The user is
 *   unidentifiable; the caller must block Sheets writes rather than guess so no
 *   junk row reaches the system of record (TECH-SPEC §2.1, BOOT-03).
 * - `Role` missing or empty → the identity is still returned, with an empty
 *   `roleId`. Progress is keyed by `employeeId` alone in
 *   `server/sheets/snapshot.ts`, `GET /api/progress` already defaults `role`
 *   to `''` in `server/routes/progress.ts`, and the snapshot echoes `roleId`
 *   back unused — so a role-less identity is still safe to write with. What it
 *   is not safe to do is pick a curriculum; that decision belongs to the
 *   caller, which must show the "no training for this role yet" screen.
 * - Nothing is defaulted. TECH-SPEC §2.1: "Never silently fall back to a
 *   default curriculum" — a whole job function trained on the wrong content is
 *   undetectable. (`use-role.ts` does fall back to `storekeeper`; that is the
 *   demo-role vocabulary, and it is not a precedent to copy here.)
 */
/**
 * Reads one boot param, tolerating any letter case in the NAME.
 *
 * Exact match first, so a URL holding both `Role` (the host's) and `role` (a
 * lesson demo slug) resolves to the host's rather than to whichever came
 * first. Falls back to the first case-insensitive match, which is also what
 * `URLSearchParams.get()` would return for a repeated parameter.
 *
 * Exported for `./identity-url.ts`, which must find the key the URL actually
 * uses so it re-attaches onto that key instead of adding a second, differently
 * cased duplicate.
 */
export function findParamKey(params: URLSearchParams, name: string): string | null {
  if (params.has(name)) return name
  const wanted = name.toLowerCase()
  for (const key of params.keys()) {
    if (key.toLowerCase() === wanted) return key
  }
  return null
}

function lookup(params: URLSearchParams, name: string): string {
  const key = findParamKey(params, name)
  return key === null ? '' : (params.get(key)?.trim() ?? '')
}

// The role value is passed through as sent. RA (Retail Assistant) and SK
// (Store Keeper) are separate roles with separate curricula; the combined
// RA_SK code is no longer accepted (claim quick-kayinleong-096, which reverses
// 093's RA -> RA_SK alias).
export function readIdentity(search: string): SessionIdentity | null {
  const params = new URLSearchParams(search)
  const employeeId = lookup(params, EMPLOYEE_PARAM)
  if (employeeId === '') return null
  return { employeeId, roleId: lookup(params, ROLE_PARAM) }
}

function captureFromLocation(): SessionIdentity | null {
  // The `unit` vitest project runs in node, and nothing stops a server-side or
  // script import reaching this file. Importing it must never throw.
  if (typeof window === 'undefined') return null
  return readIdentity(window.location.search)
}

/**
 * Captured once, at module load — see the file header for why it cannot be
 * re-read per render. Mutable ONLY so the test seam below can drive it.
 */
let captured: SessionIdentity | null = captureFromLocation()

/** The identity captured at boot, or `null` if the host app sent none. */
export function getSessionIdentity(): SessionIdentity | null {
  return captured
}

/**
 * Whether the host app identified this user.
 *
 * Callers MUST gate Sheets writes on this (TECH-SPEC §2.1, BOOT-02/BOOT-03):
 * an unidentified session may browse the sandbox freely, but must never append
 * a progress row keyed to nobody.
 */
export function isIdentified(): boolean {
  return captured !== null
}

/**
 * TEST SEAM — not production API, and not to be called from `src/` outside a
 * `*.test.*` file.
 *
 * The boot capture runs at module load, long before any test can set
 * `window.location`, so a test that needs a specific identity installs one
 * here. Always restore with `resetSessionIdentityForTests()` in an `afterEach`,
 * or the override leaks into every later test in the file.
 */
export function setSessionIdentityForTests(next: SessionIdentity | null): void {
  captured = next
}

/**
 * TEST SEAM — not production API. Re-runs the boot capture against the current
 * `window`, undoing `setSessionIdentityForTests()`. Pair it with
 * `vi.stubGlobal('window', …)` to exercise a specific boot URL.
 */
export function resetSessionIdentityForTests(): void {
  captured = captureFromLocation()
}
